import { useQsFilters } from "@dash/filters";
import { useCallback, useEffect, useRef } from "react";

import { CustomerFilters, customerFiltersSerializer, CustomerPresetFilters } from "@megaron/megarax-uri";

export const useCustomersFilters = (
  presetFilters: CustomerPresetFilters,
  onUpdatePresetFilters: (newFilters: CustomerPresetFilters) => void,
) => {
  const isPresetUpdatedOnMount = useRef(false);

  const defaultFilters: CustomerFilters = {
    page: 0,
    searchText: "",
    sort: presetFilters.sort,
    isPro: presetFilters.isPro,
    isAffiliate: presetFilters.isAffiliate,
    isActive: presetFilters.isActive,
    hasGooglePlaceId: presetFilters.hasGooglePlaceId,
    pos: presetFilters.pos,
    tags: presetFilters.tags,
    isMyRegion: presetFilters.isMyRegion,
    regions: presetFilters.regions,
    isUnassigned: presetFilters.isUnassigned,
    scannedProducts: presetFilters.scannedProducts,
    status: presetFilters.status,
    categories: presetFilters.categories,
    parents: presetFilters.parents,
    relation: presetFilters.relation,
    loyaltyJoinedAtStartDate: presetFilters.loyaltyJoinedAtStartDate,
    loyaltyJoinedAtEndDate: presetFilters.loyaltyJoinedAtEndDate,
  };

  const { filters, setFilter, setFilters } = useQsFilters(customerFiltersSerializer, defaultFilters);

  useEffect(() => {
    if (!isPresetUpdatedOnMount.current) {
      onUpdatePresetFilters(parseFiltersToPresetFilters(filters));
      isPresetUpdatedOnMount.current = true;
    }
  }, [filters, isPresetUpdatedOnMount, onUpdatePresetFilters]);

  const newSetFilter =
    <Tkey extends keyof CustomerFilters>(key: Tkey) =>
    (value: CustomerFilters[Tkey]) => {
      setFilter(key)(value);

      if (key === "page" || key === "searchText") {
        return;
      }

      onUpdatePresetFilters({ ...presetFilters, [key]: value });
    };

  const newSetFilters = useCallback(
    (newFilters: CustomerFilters) => {
      setFilters(newFilters);
      onUpdatePresetFilters(parseFiltersToPresetFilters(newFilters));
    },
    [onUpdatePresetFilters, setFilters],
  );

  return { filters, setFilter: newSetFilter, defaultFilters, setFilters: newSetFilters };
};

const parseFiltersToPresetFilters = (filters: CustomerFilters): CustomerPresetFilters => {
  const { page, searchText, ...rest } = filters;

  return rest;
};
